import { createSelector } from 'reselect'
import { composeGetters } from 'react-redux-getters'
import moment from 'moment'

import orderBy from 'lodash/orderBy'
import filter from 'lodash/filter'
import last from 'lodash/last'
import map from 'lodash/map'
import find from 'lodash/find'
import mapValues from 'lodash/mapValues'
import some from 'lodash/some'

import * as liveChatStatuses from 'constants/liveChatStatuses'
import * as liveChatMessageTypes from 'constants/liveChatMessageTypes'
import * as liveChatMessageStatuses from 'constants/liveChatMessageStatuses'
import { getRequests } from 'selectors/requests'
import { getLiveChats } from 'selectors/liveChats'
import { getProposals } from 'selectors/proposals'

import { FILTER_STATUSES, LIVE_CHAT, REQUEST, STATUS_ALL, STATUS_UNREAD } from 'constants/inbox'

const isRequestRead = request => !request.unreadCounter

const isLiveChatRead = liveChat => !some(
  liveChat.data.messages,
  message => (
    message.status !== liveChatMessageStatuses.READ
    && message.type === liveChatMessageTypes.INCOMING
  )
)

const filterRequests = (requests, inboxStatus, proposalId) => filter(requests, (request) => {
  // Ignore requests with other proposal
  if (proposalId && request.proposalId !== proposalId) return false
  let requestRead
  return some(FILTER_STATUSES[inboxStatus][REQUEST], ({ status, read }) => {
    // Check status
    if (status !== request.status) return false
    // Check read marker
    if (read !== undefined) {
      // Prevent multiple calculations
      if (requestRead === undefined) requestRead = isRequestRead(request)
      if (read !== requestRead) return false
    }
    // Otherwise return true
    return true
  })
})

const filterLiveChats = (liveChats, inboxStatus, proposalId) => filter(liveChats, (liveChat) => {
  const { data, queryId: requestId } = liveChat
  const { initialLivechatProposalId } = data
  // Ignore livechats with request
  if (requestId) return false
  // Ignore chats with other proposal
  if (proposalId && initialLivechatProposalId !== proposalId) return false
  let liveChatRead
  return some(FILTER_STATUSES[inboxStatus][LIVE_CHAT], ({ status, read }) => {
    // Check status
    if (status !== liveChat.status) return false
    // Check read marker
    if (read !== undefined) {
      // Prevent multiple calculations
      if (liveChatRead === undefined) liveChatRead = isLiveChatRead(liveChat)
      if (read !== liveChatRead) return false
    }
    // Otherwise return true
    return true
  })
})

const createRequestItem = proposals => request => ({
  type: REQUEST,
  key: request.id,
  proposal: find(proposals, { id: request.proposalId }),
  request
})

const createLiveChatItem = proposals => liveChat => ({
  type: LIVE_CHAT,
  key: liveChat.uuid,
  proposal: find(proposals, { id: liveChat.data.initialLivechatProposalId }),
  liveChat,
})

const getItemSortDate = ({ type, liveChat, request }) => {
  if (type === REQUEST) {
    // Requests
    const { lastClientEmail, createdAt } = request
    return (lastClientEmail && lastClientEmail.createdAt) || createdAt
  }
  // Livechats
  const { status, data: { messages } } = liveChat
  const lastMessage = last(messages) || {}
  const date = lastMessage.date || (new Date(0)).toISOString()
  if (status !== liveChatStatuses.NEW) {
    return date
  }
  return moment(date).add(1, 'day').toISOString()
}

const orderItems = items => orderBy(items, [getItemSortDate], 'desc')

export const makeGetInboxItems = () => createSelector(
  state => getRequests(state),
  state => getLiveChats(state),
  state => getProposals(state),
  (state, { proposalId }) => proposalId,
  (state, { status }) => status || STATUS_ALL,
  (requestsGetter, liveChatsGetter, proposalsGetter, proposalId, status) => composeGetters(
    requestsGetter, liveChatsGetter, proposalsGetter,
    (requests, liveChats, proposals) => {
      const filteredRequests = filterRequests(requests, status, proposalId)
      const requestItems = map(filteredRequests, createRequestItem(proposals))
      const filteredLiveChats = filterLiveChats(liveChats, status, proposalId)
      const liveChatItems = map(filteredLiveChats, createLiveChatItem(proposals))
      return orderItems([...liveChatItems, ...requestItems])
    }
  )
)

export const makeGetInboxItemsCount = () => {
  return createSelector(
    makeGetInboxItems(),
    inboxItemsGetter => composeGetters(
      inboxItemsGetter,
      inboxItems => inboxItems.length
    )
  )
}

export const getUnreadInboxItemsCount = createSelector(
  getRequests,
  getLiveChats,
  (requestsGetter, liveChatsGetter) => composeGetters(
    requestsGetter, liveChatsGetter,
    (requests, liveChats) => {
      const unreadRequests = filterRequests(requests, STATUS_UNREAD)
      const unreadLiveChats = filterLiveChats(liveChats, STATUS_UNREAD)
      return unreadRequests.length + unreadLiveChats.length
    }
  )
)

export const getUnfilteredInboxItems = createSelector(
  getRequests,
  getLiveChats,
  getProposals,
  (requestsGetter, liveChatsGetter, proposalsGetter) => composeGetters(
    requestsGetter, liveChatsGetter, proposalsGetter,
    (requests, liveChats, proposals) => {
      const requestItems = mapValues(requests, createRequestItem(proposals))
      const liveChatItems = mapValues(liveChats, createLiveChatItem(proposals))
      return { ...liveChatItems, ...requestItems }
    }
  )
)
